import React from 'react';
import { LanguagePicker } from '../components/LanguagePicker';
import { Text, PulseCircle } from '../components/ds';
import { useLanguage } from '../i18n';

const APP_STORE_URL = 'https://apps.apple.com/app/id0000000000';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=live.iamok.app';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-surface-bg flex flex-col">
      <NavBar />
      <main className="flex-1">
        <Hero />
        <HowItWorks />
        <Reassurance />
        <ClosingCTA />
      </main>
      <Footer />
    </div>
  );
};

const NavBar: React.FC = () => {
  const { t } = useLanguage();
  return (
    <header className="sticky top-0 z-20 bg-surface-card/80 backdrop-blur border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="/" className="inline-flex items-center gap-2">
          <Logo />
        </a>
        <div className="flex items-center gap-3 sm:gap-6">
          <nav className="hidden sm:flex items-center gap-6">
            <a href="#how" className="text-text-secondary hover:text-text-primary text-body transition-base">
              {t('landing.nav.howItWorks')}
            </a>
            <a href="#privacy" className="text-text-secondary hover:text-text-primary text-body transition-base">
              {t('landing.nav.privacy')}
            </a>
          </nav>
          {/* Outside the nav that collapses on small screens: a visitor
              deciding whether to install anything should be able to read the
              pitch in their own language first, on a phone as much as on a
              desktop. */}
          <LanguagePicker />
          <a
            href={APP_STORE_URL}
            className="hidden sm:inline-flex rounded-full bg-brand-secondary text-white px-4 py-2 text-label font-semibold hover:opacity-90 transition-base"
          >
            {t('landing.nav.getTheApp')}
          </a>
          <a
            href={APP_STORE_URL}
            className="sm:hidden rounded-full bg-brand-secondary text-white px-4 py-2 text-label font-semibold"
          >
            {t('landing.nav.getTheApp.short')}
          </a>
        </div>
      </div>
    </header>
  );
};

const Logo: React.FC = () => (
  <span className="font-display text-heading text-brand-primary tracking-tight">iam.ok</span>
);

const Hero: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 flex flex-col gap-6 animate-fade-up">
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-brand-secondary-soft px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-brand-secondary" />
              <span className="text-label font-medium text-brand-primary">
                {t('landing.hero.badge')}
              </span>
            </span>
            <h1 className="text-[44px] leading-[1.05] sm:text-[60px] sm:leading-[1.04] font-display text-text-primary tracking-tight">
              {t('landing.hero.headline.first')}
              <br />
              <span className="text-brand-secondary">{t('landing.hero.headline.second')}</span>
            </h1>
            <Text variant="body-lg" color="secondary" className="max-w-xl">
              {t('landing.hero.body')}
            </Text>
            <div className="flex flex-col sm:flex-row gap-3 mt-2">
              <a
                href={APP_STORE_URL}
                className="rounded-full bg-brand-secondary text-white px-6 py-4 text-body-lg font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-base shadow-elev-2"
              >
                <PlusIcon />
                {t('landing.hero.cta.primary')}
              </a>
              <a
                href="#how"
                className="rounded-full bg-surface-card border border-gray-200 text-text-primary px-6 py-4 text-body-lg font-semibold inline-flex items-center justify-center gap-2 hover:bg-gray-100 transition-base"
              >
                {t('landing.hero.cta.secondary')}
              </a>
            </div>
            <div className="flex items-center gap-5 mt-4 flex-wrap">
              <Trust label={t('landing.hero.trust.noAccount')} />
              <Trust label={t('landing.hero.trust.noAppForContacts')} />
              <Trust label={t('landing.hero.trust.encryptedLinks')} />
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center lg:justify-end">
            <HeroVisual />
          </div>
        </div>
      </div>

      {/* soft glow background */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-brand-secondary-soft blur-3xl opacity-60"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-40 w-[400px] h-[400px] rounded-full bg-brand-accent-soft blur-3xl opacity-40"
      />
    </section>
  );
};

const HeroVisual: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="relative">
      <PulseCircle tone="ok" size="xl" animate>
        <HeartIcon />
      </PulseCircle>
      {/* floating status pill */}
      <div className="absolute -left-4 top-8 sm:-left-10 bg-surface-card rounded-full shadow-elev-2 px-4 py-2 flex items-center gap-2 animate-float-soft">
        <span className="w-2 h-2 rounded-full bg-status-ok" />
        <Text variant="label" color="primary">
          {t('landing.hero.visual.checkName')}
        </Text>
      </div>
      <div
        className="absolute -right-2 bottom-6 sm:-right-4 bg-surface-card rounded-md shadow-elev-2 px-4 py-3 flex items-center gap-3 animate-float-soft"
        style={{ animationDelay: '1.2s' }}
      >
        <span className="w-8 h-8 rounded-full bg-status-ok-soft inline-flex items-center justify-center">
          <CheckIcon />
        </span>
        <div className="flex flex-col leading-tight">
          <Text variant="label" color="primary">
            {t('landing.hero.visual.pingReceived')}
          </Text>
          <Text variant="caption" color="muted">
            {t('landing.hero.visual.pingTime')}
          </Text>
        </div>
      </div>
    </div>
  );
};

const Trust: React.FC<{ label: string }> = ({ label }) => (
  <div className="inline-flex items-center gap-2">
    <span className="w-5 h-5 rounded-full bg-brand-secondary-soft inline-flex items-center justify-center">
      <CheckIcon small />
    </span>
    <Text variant="label" color="secondary">
      {label}
    </Text>
  </div>
);

const HowItWorks: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section id="how" className="py-20 sm:py-28 bg-surface-card border-y border-gray-200">
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col items-center text-center gap-3 mb-14">
          <Text variant="label" color="secondary" className="uppercase tracking-widest">
            {t('landing.how.eyebrow')}
          </Text>
          <h2 className="font-display text-title sm:text-display text-text-primary tracking-tight">
            {t('landing.how.title')}
          </h2>
          <Text variant="body-lg" color="muted" className="max-w-xl">
            {t('landing.how.body')}
          </Text>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <Step
            n="01"
            title={t('landing.how.step1.title')}
            body={t('landing.how.step1.body')}
            icon={<CreateIcon />}
          />
          <Step
            n="02"
            title={t('landing.how.step2.title')}
            body={t('landing.how.step2.body')}
            icon={<PingIcon />}
          />
          <Step
            n="03"
            title={t('landing.how.step3.title')}
            body={t('landing.how.step3.body')}
            icon={<BellIcon />}
          />
        </div>
      </div>
    </section>
  );
};

const Step: React.FC<{ n: string; title: string; body: string; icon: React.ReactNode }> = ({
  n,
  title,
  body,
  icon,
}) => (
  <div className="group bg-surface-bg rounded-lg p-6 border border-gray-200 hover:border-brand-secondary hover:shadow-elev-2 transition-base flex flex-col gap-4">
    <div className="flex items-center justify-between">
      <span className="w-12 h-12 rounded-full bg-brand-secondary-soft inline-flex items-center justify-center text-brand-secondary group-hover:bg-brand-secondary group-hover:text-white transition-base">
        {icon}
      </span>
      <Text variant="label" color="muted" tabular>
        {n}
      </Text>
    </div>
    <Text as="h3" variant="heading">
      {title}
    </Text>
    <Text variant="body" color="secondary">
      {body}
    </Text>
  </div>
);

const Reassurance: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section id="privacy" className="py-20 sm:py-28">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="flex flex-col gap-5">
            <Text variant="label" color="secondary" className="uppercase tracking-widest">
              {t('landing.privacy.eyebrow')}
            </Text>
            <h2 className="font-display text-title sm:text-display text-text-primary tracking-tight">
              {t('landing.privacy.title')}
            </h2>
            <Text variant="body-lg" color="secondary">
              {t('landing.privacy.body')}
            </Text>
            <ul className="flex flex-col gap-3 mt-2">
              <Bullet>{t('landing.privacy.bullet1')}</Bullet>
              <Bullet>{t('landing.privacy.bullet2')}</Bullet>
              <Bullet>{t('landing.privacy.bullet3')}</Bullet>
            </ul>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-surface-card rounded-lg shadow-elev-3 p-6 max-w-sm w-full border border-gray-200 animate-fade-up">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-10 rounded-full bg-status-ok-soft inline-flex items-center justify-center">
                  <CheckIcon />
                </span>
                <div className="flex flex-col leading-tight">
                  <Text variant="label" color="primary">
                    {t('landing.privacy.sample.checkName')}
                  </Text>
                  <Text variant="caption" color="muted">
                    {t('landing.privacy.sample.meta')}
                  </Text>
                </div>
              </div>
              <div className="h-px bg-gray-200 my-4" />
              <Text variant="caption" color="muted" className="uppercase tracking-widest mb-3 block">
                {t('landing.privacy.sample.activityTitle')}
              </Text>
              <ul className="flex flex-col gap-3">
                <ActivityRow
                  status="ok"
                  title={t('landing.privacy.sample.pingReceived')}
                  time={t('landing.privacy.sample.time1')}
                />
                <ActivityRow
                  status="ok"
                  title={t('landing.privacy.sample.pingReceived')}
                  time={t('landing.privacy.sample.time2')}
                />
                <ActivityRow
                  status="neutral"
                  title={t('landing.privacy.sample.checkStarted')}
                  time={t('landing.privacy.sample.time3')}
                />
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-1 w-5 h-5 rounded-full bg-brand-secondary-soft inline-flex items-center justify-center flex-shrink-0">
      <CheckIcon small />
    </span>
    <Text variant="body" color="primary">
      {children}
    </Text>
  </li>
);

const ActivityRow: React.FC<{ status: 'ok' | 'neutral'; title: string; time: string }> = ({
  status,
  title,
  time,
}) => {
  const bg = status === 'ok' ? 'bg-status-ok-soft' : 'bg-brand-secondary-soft';
  return (
    <li className="flex items-center gap-3">
      <span className={`w-8 h-8 rounded-full inline-flex items-center justify-center ${bg}`}>
        {status === 'ok' ? <CheckIcon /> : <span className="w-3 h-3 rounded-full border-2 border-brand-secondary" />}
      </span>
      <div className="flex-1">
        <Text variant="body" color="primary">
          {title}
        </Text>
      </div>
      <Text variant="caption" color="muted" tabular>
        {time}
      </Text>
    </li>
  );
};

const ClosingCTA: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section className="pb-24 sm:pb-32">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <div className="relative overflow-hidden rounded-lg bg-brand-primary text-text-inverse p-10 sm:p-14 flex flex-col items-center text-center gap-6">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-secondary opacity-30 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-brand-accent opacity-20 blur-3xl"
          />
          <h2 className="font-display text-display tracking-tight relative">
            {t('landing.cta.title')}
          </h2>
          <Text variant="body-lg" className="text-text-inverse/80 max-w-xl relative">
            {t('landing.cta.body')}
          </Text>
          <div className="flex flex-col sm:flex-row gap-3 relative">
            <a
              href={APP_STORE_URL}
              className="rounded-full bg-brand-secondary text-text-inverse px-6 py-4 text-body-lg font-semibold inline-flex items-center justify-center gap-2 hover:opacity-90 transition-base"
            >
              {t('landing.cta.appStore')}
            </a>
            <a
              href={PLAY_STORE_URL}
              className="rounded-full bg-white/10 border border-white/20 text-text-inverse px-6 py-4 text-body-lg font-semibold inline-flex items-center justify-center gap-2 hover:bg-white/20 transition-base"
            >
              {t('landing.cta.googlePlay')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer: React.FC = () => {
  const { t } = useLanguage();
  return (
    <footer className="border-t border-gray-200 bg-surface-card">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo />
        <div className="flex items-center gap-6">
          <a href="#privacy" className="text-text-secondary hover:text-text-primary text-label transition-base">
            {t('landing.footer.privacy')}
          </a>
          <a href="#privacy" className="text-text-secondary hover:text-text-primary text-label transition-base">
            {t('landing.footer.terms')}
          </a>
          <a href="mailto:hello@iamok.live" className="text-text-secondary hover:text-text-primary text-label transition-base">
            {t('landing.footer.contact')}
          </a>
        </div>
        <Text variant="caption" color="muted">
          {t('common.footer.copyright', { year: new Date().getFullYear() })}
        </Text>
      </div>
    </footer>
  );
};

/* ----------------------------- Icons ----------------------------- */

const PlusIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const CheckIcon: React.FC<{ small?: boolean }> = ({ small }) => (
  <svg
    width={small ? 12 : 16}
    height={small ? 12 : 16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={small ? 'text-brand-secondary' : 'text-status-ok'}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const HeartIcon: React.FC = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12h4l3-9 4 18 3-9h4" />
  </svg>
);

const CreateIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const PingIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    <path d="M8.5 8.5a5 5 0 000 7M15.5 8.5a5 5 0 010 7M5.5 5.5a9 9 0 000 13M18.5 5.5a9 9 0 010 13" />
  </svg>
);

const BellIcon: React.FC = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 16v-5a6 6 0 10-12 0v5l-2 2h16l-2-2zM10 21a2 2 0 004 0" />
  </svg>
);

export default LandingPage;
